import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom"

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { AuthContext } from "../hooks/use-auth"

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '80%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignIn(props) {
  const navigate = useNavigate()
  const { signInFunc, availableTabs } = useContext(AuthContext);
  const [usernameError, setUsernameError] = React.useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [loginSuccess, setLoginSuccess] = React.useState(false);
  const [tabList, setTabList] = React.useState([]);

  React.useEffect(() => {
    if (availableTabs) {
      setTabList(availableTabs.split(',').map(s => s.trim()));
    }
  }, [availableTabs]);

  React.useEffect(() => {
    if (loginSuccess && tabList.length > 0) {
      const tabMapping = {
        // 'SummaryForCity' は "/" に遷移
        // SummaryForCity: 'summary_for_city',
        // Summary: 'summary',
        // UserRegisterForm: 'user_register_form',
        // RiskAnalysis: 'risk_analysis',
        // SoilRain: 'soil_rain',
        // Typhoon: 'typhoon',
        Risk: 'risk',
        AreaRisk: 'area_risk',
        Chronology: "chronology",
        River: 'river',
      };

      const originalTab = tabList[0];
      const redirectPath = `/${tabMapping[originalTab] || originalTab}`;

      navigate(redirectPath, { replace: true });
    }
  }, [loginSuccess, tabList, navigate]);

  const handleSubmit = async event => {
    if (usernameError || passwordError) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const result = await signInFunc(data.get('username'), data.get('password'));

    if (result !== undefined) {
      if (result.success) {
        setLoginSuccess(true);
        setErrorMessage('');
      } else {
        alert(result.message);
      }
    } else {
      setErrorMessage('ログインに失敗しました。\nユーザー名とパスワードをご確認ください。');
    }
  };
  const validateInputs = () => {
    const username = document.getElementById('username');
    const password = document.getElementById('password');

    let isValid = true;

    if (!username.value) {
      setUsernameError(true);
      setUsernameErrorMessage('ユーザー名を入力してください');
      isValid = false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage('');
    }

    if (!password.value || password.value.length < 1) {
      setPasswordError(true);
      setPasswordErrorMessage('パスワードを入力してください');
      isValid = false;
    } else if (password.value.length >= 1 && password.value.length <= 7) {
      setPasswordError(true);
      setPasswordErrorMessage('正しいパスワードを入力してください');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  // const tabList = useMemo(() => {
  //   return availableTabs ? availableTabs.split(',').map(s => s.trim()) : [];
  // }, [availableTabs]);

  return (
    <SignInContainer direction="column" justifyContent="space-between">
      <Card variant="outlined">
        {errorMessage && (
          <p style={{ color: 'red', marginTop: '5px', whiteSpace: 'pre-line' }}>{errorMessage}</p>
        )}
        <img
          src="/EDISON_LOGO_Black.png"
          alt="EDiSON Logo"
          style={{ width: 300, display: 'flex' }}
        />
        <Typography
          component="h1"
          variant="h4"
          sx={{ width: '100%', fontSize: 'clamp(1rem, 10vw, 1.4rem)' }}
        >
          ログイン
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 4,
          }}
        >
          <FormControl>
            <FormLabel htmlFor="username">ユーザー名</FormLabel>
            <TextField
              error={usernameError}
              helperText={usernameErrorMessage}
              id="username"
              name="username"
              placeholder="ユーザー名"
              autoFocus
              required
              fullWidth
              variant="outlined"
              color={usernameError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="password">パスワード</FormLabel>
            <TextField
              error={passwordError}
              helperText={passwordErrorMessage}
              name="password"
              placeholder="••••••"
              type="password"
              id="password"
              autoComplete="current-password"
              autoFocus
              required
              fullWidth
              variant="outlined"
              color={passwordError ? 'error' : 'primary'}
            />
          </FormControl>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={validateInputs}
            sx={{ height: '50px', fontSize: '1.1rem' }}
          >
            ログイン
          </Button>
        </Box>
      </Card>
    </SignInContainer>
  );
}